import { useState } from 'react';

import {
  CompanyService,
  NewLocationService,
  NewRestaurantService,
  NewService,
  Location,
  Restaurant,
  Service,
} from '../../../interfaces';
import { Button } from '@material-tailwind/react';
import { MdCreate } from 'react-icons/md';
import RestaurantServiceInputCardsContainer from '../InputComponents/RestaurantServiceInputCardsContainer';
import { Waypoint } from 'react-waypoint';

/**
 * To get an array of new restaurantService to when the placeholder IDs are removed
 * @param mapNewFRestaurantByRestaurantId - Map of key: restaurantId and value: newRestaurant
 * @param mapMapNewLocationByLocationIdByRestaurantId - Map of key: restaurantId and value: Map of key: locationId and value: newLocation
 * @param mapMapNewServiceByServiceIdByLocationId - Map of key: locationId and value: Map of key: serviceId and value: newService
 * @returns arrNewRestaurantService - Array of newRestaurantService with the placeholder IDs removed
 */
const getArrNewRestaurantService = (
  mapNewRestaurantByRestaurantId: Map<number, Restaurant>,
  mapMapNewLocationByLocationIdByRestaurantId: Map<number, Map<number, Location>>,
  mapMapNewServiceByServiceIdByLocationId: Map<number, Map<number, Service>>
): Array<NewRestaurantService> => {
  const arrNewRestaurantService: Array<NewRestaurantService> = [];
  mapNewRestaurantByRestaurantId.forEach((newRestaurant: Restaurant, restaurantId: number) => {
    const mapNewLocationByLocationId =
      mapMapNewLocationByLocationIdByRestaurantId.get(restaurantId);
    const arrNewLocationService: Array<NewLocationService> = [];
    mapNewLocationByLocationId?.forEach((newLocation: Location, locationId: number) => {
      const mapNewServiceByServiceId = mapMapNewServiceByServiceIdByLocationId.get(locationId);
      const arrNewService: Array<NewService> = [];
      mapNewServiceByServiceId?.forEach((newService: Service, serviceId: number) => {
        const newServiceWithoutPlaceholderIds: NewService = { ...newService };
        newServiceWithoutPlaceholderIds.serviceId = undefined;
        newServiceWithoutPlaceholderIds.locationId = undefined;
        newServiceWithoutPlaceholderIds.menuId = undefined;
        newServiceWithoutPlaceholderIds.menu.menuId = undefined;
        arrNewService.push(newServiceWithoutPlaceholderIds);
      });
      const newLocationService: NewLocationService = {
        ...newLocation,
        locationId: undefined,
        restaurantId: undefined,
        arrService: arrNewService,
      };
      arrNewLocationService.push(newLocationService);
    });
    const newRestaurantService: NewRestaurantService = {
      ...newRestaurant,
      restaurantId: undefined,
      arrLocationService: arrNewLocationService,
    };
    arrNewRestaurantService.push(newRestaurantService);
  });
  return arrNewRestaurantService;
};

const AddPropertyInputCard = (props: {
  companyService: CompanyService;
  saveChangesToExistingCompanyService: any;
}) => {
  const [mapNewRestaurantByRestaurantId, setMapNewRestaurantByRestaurantId] = useState(new Map());
  const [
    mapMapNewLocationByLocationIdByRestaurantId,
    setMapMapNewLocationByLocationIdByRestaurantId,
  ] = useState(new Map());
  const [mapMapNewServiceByServiceIdByLocationId, setMapMapNewServiceByServiceIdByLocationId] =
    useState(new Map());
  const [isButtonFloating, setIsButtonFloating] = useState<boolean>(false);

  /**
   * Function can add the new restaurant to map, with the restaurantId as the key, or remove the key-value if the restaurant is to be removed.
   * If the restaurant is to be removed, remove all the new location(s) and service(s) that belongs to this restaurant.
   * @param idParameters - Object containing the restaurantId of the restaurant
   * @param newRestaurant - New restaurant object, or undefined if the restaurant is to be removed
   */
  const updateMapNewRestaurantByRestaurantId = (
    idParameters: { restaurantId: number },
    newRestaurant?: Restaurant
  ) => {
    const { restaurantId } = idParameters;
    const updatedMapNewRestaurantByRestaurantId = new Map(mapNewRestaurantByRestaurantId);
    if (newRestaurant) {
      updatedMapNewRestaurantByRestaurantId.set(restaurantId, newRestaurant);
    } else {
      updatedMapNewRestaurantByRestaurantId.delete(restaurantId);

      const updatedMapMapNewLocationByLocationIdByRestaurantId = new Map(
        mapMapNewLocationByLocationIdByRestaurantId
      );
      updatedMapMapNewLocationByLocationIdByRestaurantId.delete(restaurantId);

      const updatedMapMapNewServiceByServiceIdByLocationId = new Map(
        mapMapNewServiceByServiceIdByLocationId
      );
      const lowerLimitOfRange = restaurantId - 999;
      const upperLimitOfRange = restaurantId;
      updatedMapMapNewServiceByServiceIdByLocationId.forEach(
        (mapNewServiceByService, locationId) => {
          if (locationId >= lowerLimitOfRange && locationId <= upperLimitOfRange) {
            updatedMapMapNewServiceByServiceIdByLocationId.delete(locationId);
          }
        }
      );
      setMapMapNewServiceByServiceIdByLocationId(updatedMapMapNewServiceByServiceIdByLocationId);
      setMapMapNewLocationByLocationIdByRestaurantId(
        updatedMapMapNewLocationByLocationIdByRestaurantId
      );
    }
    setMapNewRestaurantByRestaurantId(updatedMapNewRestaurantByRestaurantId);
  };

  /**
   * Function can add the new location to map, with the restaurantId as the first key and locationId as the second key, or remove the key-value if the location is
   * to be removed.
   * If the location is to be removed, remove all the new service(s) that belongs to this location.
   * @param idParameters - Object containing the locationId and the restaurantId of the location
   * @param newLocation - New location object, or undefined if the location is to be removed
   */
  const updateMapMapNewLocationByLocationIdByRestaurantId = (
    idParameters: { locationId: number; restaurantId: number },
    newLocation?: Location
  ) => {
    const { locationId, restaurantId } = idParameters;
    const updatedMapMapNewLocationByLocationIdByRestaurantId = new Map(
      mapMapNewLocationByLocationIdByRestaurantId
    );
    if (newLocation) {
      if (updatedMapMapNewLocationByLocationIdByRestaurantId.get(restaurantId)) {
        updatedMapMapNewLocationByLocationIdByRestaurantId
          .get(restaurantId)
          .set(locationId, newLocation);
      } else {
        updatedMapMapNewLocationByLocationIdByRestaurantId.set(
          restaurantId,
          new Map([[locationId, newLocation]])
        );
      }
    } else {
      updatedMapMapNewLocationByLocationIdByRestaurantId.get(restaurantId)?.delete(locationId);
      const updatedMapMapNewServiceByServiceIdByLocationId = new Map(
        mapMapNewServiceByServiceIdByLocationId
      );
      updatedMapMapNewServiceByServiceIdByLocationId.delete(locationId);
      setMapMapNewServiceByServiceIdByLocationId(updatedMapMapNewServiceByServiceIdByLocationId);
    }
    setMapMapNewLocationByLocationIdByRestaurantId(
      updatedMapMapNewLocationByLocationIdByRestaurantId
    );
  };

  /**
   * Function can add the new service to map, with the locationId as the first key and serviceId as the second key, or remove the key-value if the service
   * is to be removed
   * @param idParameters - Object containing the serviceId and the locationId of the service
   * @param newService - New service object, or undefined if the service is to be removed
   */
  const updateMapMapNewServiceByServiceIdByLocationId = (
    idParameters: { serviceId: number; locationId: number },
    newService?: Location
  ) => {
    const { locationId, serviceId } = idParameters;
    const updatedMapMapNewServiceByServiceIdByLocationId = new Map(
      mapMapNewServiceByServiceIdByLocationId
    );
    if (newService) {
      if (updatedMapMapNewServiceByServiceIdByLocationId.get(locationId)) {
        updatedMapMapNewServiceByServiceIdByLocationId.get(locationId).set(serviceId, newService);
      } else {
        updatedMapMapNewServiceByServiceIdByLocationId.set(
          locationId,
          new Map([[serviceId, newService]])
        );
      }
    } else {
      updatedMapMapNewServiceByServiceIdByLocationId.get(locationId)?.delete(serviceId);
    }
    setMapMapNewServiceByServiceIdByLocationId(updatedMapMapNewServiceByServiceIdByLocationId);
  };

  /**
   * Function is run by save changes button, and calls saveChangesToExistingCompanyService function that is
   * passed down from parent CompanyCard component. Function saves newly added restaurantServices for the
   * company.
   */
  const saveChangesForAddRestaurantServiceOnClick = async () => {
    const arrNewRestaurantService = getArrNewRestaurantService(
      mapNewRestaurantByRestaurantId,
      mapMapNewLocationByLocationIdByRestaurantId,
      mapMapNewServiceByServiceIdByLocationId
    );
    const isSaved = await props.saveChangesToExistingCompanyService(
      { companyId: props.companyService.companyId },
      {
        arrRestaurantServiceToBeCreated: arrNewRestaurantService,
      },
      {}
    );
    // Reset changes if the restaurantService(s) has been added successfully
    if (isSaved) {
      setMapNewRestaurantByRestaurantId(new Map());
      setMapMapNewLocationByLocationIdByRestaurantId(new Map());
      setMapMapNewServiceByServiceIdByLocationId(new Map());
    }
  };

  return (
    <div className="flex flex-col">
      <Button
        onClick={saveChangesForAddRestaurantServiceOnClick}
        size="sm"
        color="green"
        className={`${
          isButtonFloating && 'fixed top-20 z-10'
        } SaveChangesButton bg-blue-600 w-fit self-end flex gap-x-1 pt-2 p-2 font-medium h-fit'}`}
      >
        <MdCreate className="self-center" />
        Save Changes
      </Button>
      <Waypoint
        onEnter={() => setIsButtonFloating(false)}
        onLeave={() => setIsButtonFloating(true)}
      />
      <RestaurantServiceInputCardsContainer
        arrNewRestaurant={Array.from(mapNewRestaurantByRestaurantId.values())}
        companyId={props.companyService.companyId}
        mapMapNewLocationByLocationIdByRestaurantId={mapMapNewLocationByLocationIdByRestaurantId}
        mapMapNewServiceByServiceIdByLocationId={mapMapNewServiceByServiceIdByLocationId}
        updateMapForNewRestaurant={updateMapNewRestaurantByRestaurantId}
        updateMapMapNewLocationByLocationIdByRestaurantId={
          updateMapMapNewLocationByLocationIdByRestaurantId
        }
        updateMapMapNewServiceByServiceIdByLocationId={
          updateMapMapNewServiceByServiceIdByLocationId
        }
      />
    </div>
  );
};

export default AddPropertyInputCard;
